/* eslint-disable camelcase */

import { useMemo } from 'react';
import Decimal from 'decimal.js';
import type { PricingTable } from '@hotjar/webapp/domains/revenue/pricing/types';
import type { SiteAccountDTO, OwnedAccountDTO } from '@hotjar/account';
import type { Settings } from '@hotjar/webapp/domains/common/settings';
import {
  PricingRepository,
  sortPricingTable,
} from '@hotjar/webapp/domains/revenue/pricing/pricing-repository';

import { useProductCatalog } from '@hotjar/product-catalog';
import { useSubscriptionInfo } from '@hotjar/subscription-management';
import { useSettings } from './useSettings';
import { isBillingV3Enabled } from '../domains/billing/features';
import { keysToSnakeCase } from '../lib/convert-string';
import { productCatalogToPricingTable } from '../domains/revenue/utils';

const DEFAULT_BILLING_PERIOD = 'MONTHLY';
const DEFAULT_CURRENCY = 'EUR';

export interface UsePricingTableResult {
  pricingTable: PricingTable;
  priceOrderedPlans: [string, Decimal][];
  planLt: (lhs: string, rhs: string) => boolean;
  planGt: (lhs: string, rhs: string) => boolean;
  isLoading: boolean;
}

const createPlanComparators = (pricingTable: PricingTable) => {
  return {
    planLt: (lhs: string, rhs: string): boolean =>
      pricingTable?.[lhs]?.lt(pricingTable[rhs] ?? 0) ?? false,
    planGt: (lhs: string, rhs: string): boolean =>
      pricingTable?.[lhs]?.gt(pricingTable[rhs] ?? 0) ?? false,
  };
};

const FALLBACK_PRICING_TABLE: PricingTable = {
  '0': new Decimal(0),
};

export function usePricingTableByAccount(
  account: OwnedAccountDTO | SiteAccountDTO | undefined,
): UsePricingTableResult {
  const { data: settings, isLoading: isSettingsLoading } = useSettings();
  const isBillingV3 = account ? isBillingV3Enabled(keysToSnakeCase(account)) : false;
  const { data: subscription, isFetched: isSubscriptionLoading } = useSubscriptionInfo({
    enabled: isBillingV3,
  });
  const { data: V3ProductCatalog, isFetched } = useProductCatalog({
    billingPeriod: DEFAULT_BILLING_PERIOD,
    currency: subscription?.customer?.currency || DEFAULT_CURRENCY,
    enabled: isBillingV3,
  });

  const isLoading = isFetched && isSubscriptionLoading && isSettingsLoading;

  return useMemo(() => {
    const { pricing_tables } = settings ?? ({} as Settings);

    const pricing = new PricingRepository(pricing_tables);
    const pricingTable = pricing.byAccount(account);

    if (isBillingV3) {
      const pricingTableFromV3Catalog =
        productCatalogToPricingTable(V3ProductCatalog) ?? FALLBACK_PRICING_TABLE;
      return {
        pricingTable: pricingTableFromV3Catalog,
        priceOrderedPlans: sortPricingTable(pricingTableFromV3Catalog),
        ...createPlanComparators(pricingTableFromV3Catalog),
        isLoading,
      };
    }

    return {
      pricingTable,
      priceOrderedPlans: sortPricingTable(pricingTable),
      ...createPlanComparators(pricingTable),
      isLoading,
    };
  }, [settings, account, isBillingV3, isLoading, V3ProductCatalog]);
}
